import React, { useEffect, useMemo, useState } from "react";
import { HERO_AUTOMATION_CALLOUTS } from "../../landingContent";
import { scaleHeroPreviewDelayMs } from "../../heroPreviewSpeed";

const PLACEMENT_CLASS = {
  left: "landing-v2-present-hero__callout--left",
  "lower-left": "landing-v2-present-hero__callout--lower-left",
  "agenda-right": "landing-v2-present-hero__callout--agenda-right",
  "top-right": "landing-v2-present-hero__callout--top-right",
  "center-right": "landing-v2-present-hero__callout--center-right",
  "bottom-right": "landing-v2-present-hero__callout--bottom-right",
  "top-center": "landing-v2-present-hero__callout--top-center",
  center: "landing-v2-present-hero__callout--center",
  "bottom-left": "landing-v2-present-hero__callout--bottom-left",
  "browser-companion": "landing-v2-present-hero__callout--browser-companion",
  "below-timer": "landing-v2-present-hero__callout--below-timer",
};

const renderCallout = (activeEventId, item) => (
  <article
    key={`${activeEventId}-${item.id}`}
    className={[
      "landing-v2-present-hero__callout",
      PLACEMENT_CLASS[item.placement] || "",
      `landing-v2-present-hero__callout--tone-${item.tone}`,
      item.popDelayMs ? "landing-v2-present-hero__callout--pop-delay" : "",
    ].join(" ")}
    style={item.popDelayMs ? {
      "--callout-pop-delay": `${item.popDelayMs}ms`,
    } : undefined}
  >
    {item.icon ? (
      <span className="landing-v2-present-hero__callout-icon" aria-hidden="true">
        <i className={`fa ${item.icon}`} />
      </span>
    ) : null}
    <strong className="landing-v2-present-hero__callout-title">{item.title}</strong>
  </article>
);

const PresentScreenHeroCallouts = ({ activeEventId, isVisible, previewSpeedMultiplier = 1 }) => {
  const calloutConfig = HERO_AUTOMATION_CALLOUTS[activeEventId];
  const [rotateIndex, setRotateIndex] = useState(0);
  const [showRepeatCallout, setShowRepeatCallout] = useState(false);
  const calloutRotateMs = calloutConfig?.rotateMs
    ? scaleHeroPreviewDelayMs(calloutConfig.rotateMs, previewSpeedMultiplier)
    : undefined;

  useEffect(() => {
    setRotateIndex(0);
    setShowRepeatCallout(false);
  }, [activeEventId]);

  useEffect(() => {
    if (
      !isVisible
      || calloutConfig?.showTogether
      || !calloutRotateMs
      || calloutConfig.items.length <= 1
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRotateIndex((currentIndex) => (
        currentIndex + 1 >= calloutConfig.items.length ? 0 : currentIndex + 1
      ));
    }, calloutRotateMs);

    return () => window.clearInterval(intervalId);
  }, [activeEventId, calloutConfig, calloutRotateMs, isVisible]);

  useEffect(() => {
    if (!isVisible || !calloutConfig?.repeatCallout?.showAfterMs) {
      setShowRepeatCallout(false);
      return undefined;
    }

    setShowRepeatCallout(false);

    const timeoutId = window.setTimeout(() => {
      setShowRepeatCallout(true);
    }, scaleHeroPreviewDelayMs(calloutConfig.repeatCallout.showAfterMs, previewSpeedMultiplier));

    return () => window.clearTimeout(timeoutId);
  }, [activeEventId, calloutConfig, isVisible, previewSpeedMultiplier]);

  const visibleItems = useMemo(() => {
    if (showRepeatCallout) {
      return [];
    }

    if (!calloutConfig?.items?.length) {
      return [];
    }

    if (calloutConfig.showTogether) {
      return calloutConfig.items;
    }

    if (calloutRotateMs && calloutConfig.items.length > 1) {
      return [calloutConfig.items[rotateIndex]];
    }

    return [calloutConfig.items[0]];
  }, [calloutConfig, calloutRotateMs, rotateIndex, showRepeatCallout]);

  if (!isVisible || (visibleItems.length === 0 && !showRepeatCallout)) {
    return null;
  }

  return (
    <div className="landing-v2-present-hero__callouts" aria-live="polite">
      {visibleItems.map((item) => renderCallout(activeEventId, item))}
      {showRepeatCallout && calloutConfig?.repeatCallout
        ? renderCallout(activeEventId, calloutConfig.repeatCallout)
        : null}
    </div>
  );
};

export default PresentScreenHeroCallouts;
